import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import UserGuideModal from '../settings-and-privacy/components/UserGuideModal';
import ContactSupportModal from '../settings-and-privacy/components/ContactSupportModal';
import FAQsModal from '../settings-and-privacy/components/FAQsModal';
import VideoTutorialsModal from '../settings-and-privacy/components/VideoTutorialsModal';
import BottomTabNavigation from '../../components/navigation/BottomTabNavigation';
import { useAuth } from '../../contexts/AuthContext';
import CloseButton from '../../components/CloseButton';
import LogoutButton from '../../components/LogoutButton';

const HelpAndSupport = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [activeModal, setActiveModal] = useState(null);

  const helpItems = [
    {
      icon: 'BookOpen',
      title: 'User Guide',
      description: 'Learn how to use all features step-by-step',
      longDescription: 'Comprehensive guide covering getting started, daily check-ins, symptom tracking, analytics, caregiver features, subscription plans, and privacy settings.',
      action: 'View Guide',
      modalKey: 'userGuide',
      color: 'bg-blue-500/10',
      iconColor: 'text-blue-600'
    },
    {
      icon: 'HelpCircle',
      title: 'FAQs',
      description: 'Find answers to common questions',
      longDescription: 'Quick answers to frequently asked questions about getting started, daily check-ins, data export, subscriptions, caregiver features, referral program, privacy, and analytics.',
      action: 'View FAQs',
      modalKey: 'faqs',
      color: 'bg-purple-500/10',
      iconColor: 'text-purple-600'
    },
    {
      icon: 'Video',
      title: 'Video Tutorials',
      description: 'Coming soon - Video guides will be available shortly',
      longDescription: 'We are currently preparing step-by-step video tutorials to help you get the most out of MyBodyToday. Check back soon!',
      action: 'Coming Soon',
      modalKey: null,
      color: 'bg-green-500/10',
      iconColor: 'text-green-600',
      disabled: true
    },
    {
      icon: 'MessageCircle',
      title: 'Contact Support',
      description: 'Get help from our team',
      longDescription: 'Need personalized assistance? Our support team is here to help with technical issues, billing questions, feature requests, and more.',
      action: 'Contact Us',
      modalKey: 'contactSupport',
      color: 'bg-orange-500/10',
      iconColor: 'text-orange-600'
    }
  ];

  const quickStartSteps = [
    {
      step: 1,
      title: 'Create Your Account',
      description: 'Sign up with your email and verify your account',
      icon: 'UserPlus'
    },
    {
      step: 2,
      title: 'Choose Your User Type',
      description: 'Select Individual or Caregiver based on your needs',
      icon: 'Users'
    },
    {
      step: 3,
      title: 'Complete Your First Check-In',
      description: 'Start tracking your symptoms and daily health',
      icon: 'Calendar'
    },
    {
      step: 4,
      title: 'Explore Features',
      description: 'View history, export data, and discover analytics',
      icon: 'Compass'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Help & Support - MyBodyToday</title>
        <meta name="description" content="Get help and learn how to use MyBodyToday. Access user guides, FAQs, video tutorials, and contact support." />
      </Helmet>

      <div className="min-h-screen bg-background pb-24">
        {/* Header */}
        <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-background border-b border-border">
          <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                {isAuthenticated && (
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="p-2 hover:bg-muted rounded-lg transition-colors"
                    aria-label="Go back"
                  >
                    <Icon name="ArrowLeft" size={24} />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3">
                <CloseButton 
                  navigateTo={isAuthenticated ? "/dashboard" : "/sign-in"} 
                  ariaLabel="Close help and support"
                  onClose={() => navigate(isAuthenticated ? "/dashboard" : "/sign-in")}
                />
                {isAuthenticated && <LogoutButton />}
              </div>
            </div>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-2xl bg-primary/20 flex items-center justify-center">
                <Icon name="HelpCircle" size={28} color="var(--color-primary)" strokeWidth={2} />
              </div>
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-2">
                  Help & Support
                </h1>
                <p className="text-base sm:text-lg text-muted-foreground">
                  Everything you need to get started and make the most of MyBodyToday
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Quick Start Guide */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-6">
              <Icon name="Rocket" size={24} color="var(--color-primary)" />
              <h2 className="text-2xl font-bold text-foreground">Quick Start Guide</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {quickStartSteps?.map((item) => (
                <div
                  key={item?.step}
                  className="flex items-start gap-4 p-5 rounded-xl bg-muted/50 border border-border hover:border-primary/30 hover:shadow-therapeutic transition-smooth"
                >
                  <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 flex-shrink-0">
                    <Icon name={item?.icon} size={20} color="var(--color-primary)" strokeWidth={2} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold text-primary">STEP {item?.step}</span>
                    </div>
                    <h3 className="text-base font-semibold text-foreground mb-1">
                      {item?.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {item?.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Help Resources */}
          <section className="mb-10">
            <div className="flex items-center gap-3 mb-6">
              <Icon name="LifeBuoy" size={24} color="var(--color-primary)" />
              <h2 className="text-2xl font-bold text-foreground">Help Resources</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {helpItems?.map((item, index) => (
                <button
                  key={index}
                  onClick={() => !item?.disabled && setActiveModal(item?.modalKey)}
                  disabled={item?.disabled}
                  className={`
                    flex flex-col items-start gap-4 p-6 rounded-xl
                    ${item?.color} border border-border
                    ${item?.disabled 
                      ? 'opacity-60 cursor-not-allowed' :'hover:border-primary/30 hover:shadow-therapeutic active:scale-[0.98] cursor-pointer'
                    }
                    transition-smooth text-left
                    focus:outline-none focus:ring-3 focus:ring-ring
                  `}
                  aria-label={item?.action}
                  aria-disabled={item?.disabled}
                >
                  <div className="flex items-center justify-between w-full">
                    <div className={`flex items-center justify-center w-12 h-12 rounded-lg ${item?.color} ${item?.iconColor}`}>
                      <Icon name={item?.icon} size={24} strokeWidth={2} />
                    </div>
                    {item?.disabled ? (
                      <span className="px-3 py-1 bg-muted/80 text-muted-foreground text-xs font-semibold rounded-full">
                        Coming Soon
                      </span>
                    ) : (
                      <Icon name="ArrowRight" size={20} className="text-muted-foreground" strokeWidth={2} />
                    )}
                  </div>
                  <div className="w-full">
                    <h3 className="text-lg font-bold text-foreground mb-2">
                      {item?.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      {item?.description}
                    </p>
                    <p className="text-xs text-muted-foreground/80 leading-relaxed">
                      {item?.longDescription}
                    </p>
                  </div>
                  <span className={`text-sm font-semibold inline-flex items-center gap-2 ${
                    item?.disabled ? 'text-muted-foreground' : 'text-primary'
                  }`}>
                    {item?.action}
                  </span>
                </button>
              ))}
            </div>
          </section>

          {/* Need More Help */}
          <section className="mb-6">
            <div className="p-6 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Icon name="Mail" size={20} color="var(--color-primary)" strokeWidth={2} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-foreground mb-2">
                    Still Need Help?
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Our support team is here to assist you with any questions or issues you may have.
                  </p>
                  <button
                    onClick={() => setActiveModal('contactSupport')}
                    className="px-5 py-2.5 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors inline-flex items-center gap-2"
                  >
                    <Icon name="MessageCircle" size={18} />
                    Contact Support Team
                  </button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Bottom Navigation - Only show if authenticated */}
      {isAuthenticated && <BottomTabNavigation />}

      {/* Modals */}
      <UserGuideModal
        isOpen={activeModal === 'userGuide'}
        onClose={() => setActiveModal(null)}
      />
      <ContactSupportModal
        isOpen={activeModal === 'contactSupport'}
        onClose={() => setActiveModal(null)}
      />
      <FAQsModal
        isOpen={activeModal === 'faqs'}
        onClose={() => setActiveModal(null)}
      />
      <VideoTutorialsModal
        isOpen={activeModal === 'videoTutorials'}
        onClose={() => setActiveModal(null)}
      />
    </>
  );
};

export default HelpAndSupport;